"use client";

import Image from "next/image";
import { GallerySection as GalleryType } from "@/types/home";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface GallerySectionProps {
      data: GalleryType;
}

export default function GallerySection({ data }: GallerySectionProps) {
      if (!data) return null;

      return (
            <section className="py-16 bg-gray-50 overflow-hidden">
                  <div className="container mx-auto px-4 md:px-8">
                        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
                              <div className="max-w-2xl">
                                    {data.badge && (
                                          <span className="inline-block py-1 px-3 rounded-full bg-[#A68F72]/10 text-[#A68F72] text-xs font-semibold tracking-wider uppercase mb-3 border border-[#A68F72]/20">
                                                {data.badge}
                                          </span>
                                    )}
                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                                          {data.item}
                                    </h2>
                                    <p className="text-gray-500 text-base leading-relaxed">
                                          {data.description}
                                    </p>
                              </div>

                              <div className="flex gap-3">
                                    <button className="swiper-prev-gallery w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] transition-all duration-300 disabled:opacity-30 cursor-pointer">
                                          <ArrowLeft size={18} />
                                    </button>
                                    <button className="swiper-next-gallery w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] transition-all duration-300 disabled:opacity-30 cursor-pointer">
                                          <ArrowRight size={18} />
                                    </button>
                              </div>
                        </div>

                        <Swiper
                              modules={[Navigation, Autoplay]}
                              spaceBetween={20}
                              slidesPerView={1.2}
                              centeredSlides={false}
                              loop={true}
                              navigation={{
                                    prevEl: ".swiper-prev-gallery",
                                    nextEl: ".swiper-next-gallery",
                              }}
                              autoplay={{
                                    delay: 4000,
                                    disableOnInteraction: false,
                              }}
                              breakpoints={{
                                    640: { slidesPerView: 2.2 },
                                    1024: { slidesPerView: 3.2 },
                                    1280: { slidesPerView: 4 },
                              }}
                              className="overflow-visible!"
                        >
                              {data.galleryItem.map((item) => (
                                    <SwiperSlide key={item.id}>
                                          <div className="relative w-full h-87.5 rounded-2xl overflow-hidden group shadow-md border border-gray-100">
                                                {item.media?.url && (
                                                      <Image
                                                            src={`${process.env.NEXT_PUBLIC_API_URL}${item.media.url}`}
                                                            alt={item.title}
                                                            fill
                                                            className="object-cover transition-transform duration-700 group-hover:scale-105"
                                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                      />
                                                )}
                                                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                                          </div>
                                    </SwiperSlide>
                              ))}
                        </Swiper>
                  </div>
            </section>
      );
}